import React, { useEffect, useState } from 'react';
import { MessageSquare, Clock, ChevronDown, ChevronUp, CheckCircle2, History, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fetchUserInquiries, InquiryDB } from '../../services/inquiryService';
import { t } from '../../i18n';
import { Language } from '../../types';

interface InquiryListProps {
    lang: Language;
    refreshTrigger: number;
}

export const InquiryList: React.FC<InquiryListProps> = ({ lang, refreshTrigger }) => {
    const { user } = useAuth();
    const [inquiries, setInquiries] = useState<InquiryDB[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadInquiries = async () => {
            if (!user) return;
            setIsLoading(true);
            try {
                const data = await fetchUserInquiries(user.id);
                setInquiries(data);
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };
        loadInquiries();
    }, [user, refreshTrigger]);

    const getCategoryLabel = (cat?: string) => {
        if (!cat) return lang === 'ko' ? '일반' : 'General';
    const categories: Record<string, string> = {
        general: t(lang, 'inquiryGeneral'),
        account: t(lang, 'inquiryAccount'),
        content: t(lang, 'inquiryContent'),
        error: t(lang, 'inquiryError'),
        suggestion: t(lang, 'inquirySuggestion')
    };
        return categories[cat] || cat;
    };

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (isLoading) {
        return (
            <div className="py-20 flex justify-center">
                <Loader2 className="animate-spin text-rose-500" size={32} />
            </div>
        );
    }

    if (inquiries.length === 0) {
        return (
            <div className="py-20 text-center animate-in fade-in duration-500">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-200 mx-auto mb-4">
                    <History size={32} />
                </div>
                <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">
                    {t(lang, 'noInquiryHistory')}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {inquiries.map((inquiry) => (
                <div 
                    key={inquiry.id}
                    className={`bg-white rounded-3xl border-2 transition-all duration-300 overflow-hidden ${
                        expandedId === inquiry.id ? 'border-rose-500 shadow-xl shadow-rose-50' : 'border-slate-50 md:hover:border-slate-100'
                    }`}
                >
                    <button 
                        onClick={() => setExpandedId(expandedId === inquiry.id ? null : inquiry.id)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left"
                    >
                        <div className="flex-1 min-w-0 mr-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg text-[10px] font-black uppercase tracking-wider">
                                    {getCategoryLabel(inquiry.category)}
                                </span>
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1 ${
                                    inquiry.status === 'answered' || inquiry.status === 'closed'
                                        ? 'bg-emerald-50 text-emerald-500' 
                                        : 'bg-amber-50 text-amber-500'
                                }`}>
                                    {inquiry.status === 'answered' || inquiry.status === 'closed' ? (
                                        <><CheckCircle2 size={10} /> {t(lang, 'completed')}</>
                                    ) : (
                                        <><Clock size={10} /> {t(lang, 'pending')}</>
                                    )}
                                </span>
                            </div>
                            <h4 className="text-slate-900 font-black text-base truncate tracking-tight uppercase">
                                {inquiry.title}
                            </h4>
                            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mt-1">
                                {formatDate(inquiry.created_at)}
                            </p>
                        </div>
                        <div className="text-slate-300">
                            {expandedId === inquiry.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                    </button>

                    {expandedId === inquiry.id && (
                        <div className="px-6 pb-6 space-y-4 animate-in slide-in-from-top-2 duration-300">
                            <div className="p-5 bg-slate-50 rounded-2xl">
                                <p className="text-slate-600 text-sm font-medium leading-relaxed whitespace-pre-wrap">
                                    {inquiry.content}
                                </p>
                            </div>
                            
                            {(inquiry.status === 'answered' || inquiry.status === 'closed') && inquiry.answer && (
                                <div className="p-5 bg-rose-50 rounded-2xl border border-rose-100 relative">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MessageSquare size={14} className="text-rose-500" />
                                        <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest">
                                            {t(lang, 'response')}
                                        </span>
                                        {inquiry.answered_at && (
                                            <span className="text-[10px] font-bold text-rose-300 uppercase tracking-widest ml-auto">
                                                {formatDate(inquiry.answered_at)}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-slate-800 text-sm font-bold leading-relaxed whitespace-pre-wrap">
                                        {inquiry.answer}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};
